import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { apartmentsApi } from '../api/apartments';
import { buildingsApi } from '../api/buildings';
import { heatApi } from '../api/heat';
import { downloadCsv, generateCsv } from '../utils/reportGenerator';

const MONTH_NAMES = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
];

const COL_APARTMENT = 268;
const COL_SERIAL = 128;
const GROUP_ROW_HEIGHT = 48;
const DEVICE_ROW_HEIGHT = 42;
const VIRTUAL_OVERSCAN = 10;
const NOTICE_TTL_MS = 3200;
const SEARCH_DEBOUNCE_MS = 200;

function formatMonthShort(ym) {
  const idx = Number(String(ym || '').split('-')[1]) - 1;
  if (idx < 0 || idx > 11) return ym;
  return MONTH_NAMES[idx];
}

function formatMonthLabel(ym) {
  const [y, m] = String(ym || '').split('-');
  const idx = Number(m) - 1;
  if (!y || idx < 0 || idx > 11) return ym;
  return `${MONTH_NAMES[idx]} ${y}`;
}

function formatNumber(value, digits = 3) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function buildingLabel(building) {
  if (!building) return '';
  const name = building.name || '';
  const address = building.address || '';
  if (name && address && name !== address) return `${name} — ${address}`;
  return name || address || String(building.id || '');
}

function groupRowsByApartment(rows) {
  const groups = [];
  const indexByApartment = new Map();
  for (const row of rows) {
    const key = row.apartment_id;
    if (!indexByApartment.has(key)) {
      indexByApartment.set(key, groups.length);
      groups.push({
        apartmentId: row.apartment_id,
        apartmentNumber: row.apartment_number,
        corpName: row.corp_name,
        area: row.area,
        heatAccountingLabel: row.heat_accounting_label || (row.heat_calculate_by_area ? 'по площади' : 'по УРТ'),
        heatCalculateByArea: Boolean(row.heat_calculate_by_area),
        devices: [row],
      });
    } else {
      groups[indexByApartment.get(key)].devices.push(row);
    }
  }
  return groups;
}

function cellTooltip(cell, monthLocked) {
  if (monthLocked) return 'Месяц заблокирован';
  if (!cell) return '';
  if (cell.is_copied) return 'Скопировано из предыдущего месяца';
  if (cell.is_carried) return 'Показание перенесено с предыдущего периода (ещё не зафиксировано в этом месяце)';
  if (cell.is_own) return 'Показание месяца';
  return '';
}

function formatApiError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === 'string' ? item : item?.msg || JSON.stringify(item)))
      .filter(Boolean)
      .join('; ') || fallback;
  }
  if (detail && typeof detail === 'object' && detail.message) return String(detail.message);
  return error?.message || fallback;
}

/**
 * Highlight meters whose actual readings are all zeros.
 * Uses reading_value so consumption mode does not mark Δ=0 rows after copy.
 */
function isAllZeroRow(row, months) {
  if (!months?.length) return false;
  for (const m of months) {
    const cell = row.values?.[m.month];
    const value = cell?.reading_value ?? cell?.value;
    if (value === null || value === undefined || Number.isNaN(Number(value))) return false;
    if (Number(value) !== 0) return false;
  }
  return true;
}

function applyApartmentHeatMode(rows, apartmentId, byArea) {
  return (rows || []).map((row) => {
    if (String(row.apartment_id) !== String(apartmentId)) return row;
    return {
      ...row,
      heat_calculate_by_area: byArea,
      heat_accounting_label: byArea ? 'по площади' : 'по УРТ',
    };
  });
}

function opaqueStickySx(left, z, backgroundColor, { withEdgeShadow = false, isHeader = false } = {}) {
  return {
    position: 'sticky',
    left,
    zIndex: z,
    backgroundColor,
    backgroundImage: 'none',
    // Prevent translucent stacking contexts from showing scrolled cells underneath.
    opacity: 1,
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    boxShadow: withEdgeShadow ? '6px 0 10px -8px rgba(15, 23, 42, 0.28)' : 'none',
    borderRight: withEdgeShadow ? '1px solid' : undefined,
    borderRightColor: withEdgeShadow ? 'divider' : undefined,
    ...(isHeader ? { top: 0 } : null),
  };
}

function buildFlatItems(groups, collapsedApartments) {
  const items = [];
  groups.forEach((group, groupIndex) => {
    const multi = group.devices.length > 1;
    const collapsed = Boolean(collapsedApartments[group.apartmentId]);
    const tintKey = groupIndex % 2;
    if (multi) {
      items.push({
        type: 'group',
        key: `group-${group.apartmentId}`,
        group,
        multi,
        tintKey,
        height: GROUP_ROW_HEIGHT,
      });
    }
    if (!multi || !collapsed) {
      for (const row of group.devices) {
        items.push({
          type: 'device',
          key: `device-${row.device_id}`,
          row,
          group,
          multi,
          tintKey,
          height: DEVICE_ROW_HEIGHT,
        });
      }
    }
  });
  return items;
}

function buildOffsets(items) {
  const offsets = new Array(items.length + 1);
  offsets[0] = 0;
  for (let i = 0; i < items.length; i += 1) {
    offsets[i + 1] = offsets[i] + items[i].height;
  }
  return offsets;
}

function findStartIndex(offsets, scrollTop) {
  let lo = 0;
  let hi = offsets.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (offsets[mid] <= scrollTop) lo = mid + 1;
    else hi = mid;
  }
  return Math.max(0, lo - 1);
}

export default function RteReadingsPage() {
  const theme = useTheme();
  const { buildingId: routeBuildingId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryBuildingId = searchParams.get('buildingId');
  const initialBuildingId = routeBuildingId || queryBuildingId || '';

  const [buildings, setBuildings] = useState([]);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState(initialBuildingId);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [showConsumption, setShowConsumption] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [data, setData] = useState(null);
  const [collapsedApartments, setCollapsedApartments] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [onlyZeros, setOnlyZeros] = useState(false);
  const [lockConfirm, setLockConfirm] = useState(null);
  const [savingModeApartmentId, setSavingModeApartmentId] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(560);
  const loadSeqRef = useRef(0);
  const tableContainerRef = useRef(null);
  const scrollRafRef = useRef(0);
  const noticeTimerRef = useRef(null);

  const paperBg = theme.palette.background.paper;
  const headerBg = theme.palette.mode === 'dark' ? theme.palette.background.default : paperBg;
  const tintA = theme.palette.mode === 'dark' ? '#1B2433' : '#F5F8FD';
  const tintB = theme.palette.mode === 'dark' ? '#202B3D' : '#EAF0FA';
  const zeroTint = theme.palette.mode === 'dark' ? '#3A2828' : '#FCEEEE';
  const lockedBg = theme.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.16)' : 'rgba(237, 108, 2, 0.10)';
  const copiedBg = theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.18)' : 'rgba(46, 125, 50, 0.10)';

  const showNotice = useCallback((message) => {
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    setNotice(message);
    noticeTimerRef.current = window.setTimeout(() => {
      setNotice('');
      noticeTimerRef.current = null;
    }, NOTICE_TTL_MS);
  }, []);

  useEffect(() => () => {
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    if (scrollRafRef.current) window.cancelAnimationFrame(scrollRafRef.current);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBuildingsLoading(true);
      try {
        const items = await buildingsApi.getBuildings(0, 1000);
        if (cancelled) return;
        const list = items || [];
        const heatBuildings = list.filter((b) => b.heat_allocator_enabled);
        setBuildings(heatBuildings.length ? heatBuildings : list);
      } catch (e) {
        if (!cancelled) {
          setError(formatApiError(e, 'Не удалось загрузить список домов'));
        }
      } finally {
        if (!cancelled) setBuildingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (routeBuildingId && routeBuildingId !== selectedBuildingId) {
      setSelectedBuildingId(routeBuildingId);
    }
  }, [routeBuildingId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!queryBuildingId || routeBuildingId) return;
    if (queryBuildingId !== selectedBuildingId) {
      setSelectedBuildingId(queryBuildingId);
    }
  }, [queryBuildingId, routeBuildingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedBuilding = useMemo(() => {
    const fromList = buildings.find((b) => String(b.id) === String(selectedBuildingId));
    if (fromList) return fromList;
    if (selectedBuildingId && data && String(data.building_id) === String(selectedBuildingId)) {
      return {
        id: data.building_id,
        name: data.building_name,
        address: data.building_address,
        heat_allocator_enabled: true,
      };
    }
    return null;
  }, [buildings, selectedBuildingId, data]);

  const buildingOptions = useMemo(() => {
    if (!selectedBuilding) return buildings;
    if (buildings.some((b) => String(b.id) === String(selectedBuilding.id))) return buildings;
    return [selectedBuilding, ...buildings];
  }, [buildings, selectedBuilding]);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const years = new Set([current - 2, current - 1, current, current + 1]);
    if (data?.year) years.add(Number(data.year));
    return Array.from(years).sort((a, b) => a - b);
  }, [data?.year]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!selectedBuildingId) {
      setData(null);
      return;
    }
    const seq = ++loadSeqRef.current;
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await heatApi.getReadingsMatrix(selectedBuildingId, {
        year,
        include_archived: includeArchived,
        display: showConsumption ? 'consumption' : 'readings',
      });
      if (seq !== loadSeqRef.current) return;
      setData(res);
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      if (!silent) setData(null);
      setError(formatApiError(e, 'Не удалось загрузить показания РТЭ'));
    } finally {
      if (seq === loadSeqRef.current && !silent) setLoading(false);
    }
  }, [selectedBuildingId, year, includeArchived, showConsumption]);

  useEffect(() => {
    load();
  }, [load]);

  const handleBuildingChange = (building) => {
    const nextId = building?.id ? String(building.id) : '';
    setSelectedBuildingId(nextId);
    setData(null);
    setCollapsedApartments({});
    setSearchInput('');
    setSearch('');
    setOnlyZeros(false);
    setScrollTop(0);
    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
    if (routeBuildingId) {
      if (nextId) navigate(`/building/${nextId}/rte-readings`, { replace: true });
      else navigate('/rte-readings', { replace: true });
    } else {
      const next = new URLSearchParams(searchParams);
      if (nextId) next.set('buildingId', nextId);
      else next.delete('buildingId');
      setSearchParams(next, { replace: true });
    }
  };

  const months = data?.months || [];
  const rows = data?.rows || [];

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (onlyZeros && !isAllZeroRow(row, months)) return false;
      if (!q) return true;
      const hay = [
        row.apartment_number,
        row.corp_name,
        row.serial_number,
        row.room_label,
        row.placement,
        row.heat_accounting_label,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, onlyZeros, months]);

  const zeroRowsCount = useMemo(
    () => rows.reduce((count, row) => count + (isAllZeroRow(row, months) ? 1 : 0), 0),
    [rows, months],
  );

  const groups = useMemo(() => groupRowsByApartment(filteredRows), [filteredRows]);
  const multiGroups = useMemo(() => groups.filter((g) => g.devices.length > 1), [groups]);
  const allCollapsed = multiGroups.length > 0
    && multiGroups.every((g) => Boolean(collapsedApartments[g.apartmentId]));

  const flatItems = useMemo(
    () => buildFlatItems(groups, collapsedApartments),
    [groups, collapsedApartments],
  );
  const offsets = useMemo(() => buildOffsets(flatItems), [flatItems]);
  const totalHeight = offsets[offsets.length - 1] || 0;

  const visibleRange = useMemo(() => {
    if (!flatItems.length) return { start: 0, end: 0, topPad: 0, bottomPad: 0 };
    const startRaw = findStartIndex(offsets, scrollTop);
    const start = Math.max(0, startRaw - VIRTUAL_OVERSCAN);
    let end = startRaw;
    const limit = scrollTop + viewportHeight;
    while (end < flatItems.length && offsets[end] < limit) end += 1;
    end = Math.min(flatItems.length, end + VIRTUAL_OVERSCAN);
    return {
      start,
      end,
      topPad: offsets[start],
      bottomPad: Math.max(0, totalHeight - offsets[end]),
    };
  }, [flatItems, offsets, scrollTop, viewportHeight, totalHeight]);

  const visibleItems = useMemo(
    () => flatItems.slice(visibleRange.start, visibleRange.end),
    [flatItems, visibleRange.start, visibleRange.end],
  );

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;
    const update = () => setViewportHeight(el.clientHeight || 560);
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [selectedBuildingId, loading, data]);

  useEffect(() => {
    setScrollTop(0);
    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
  }, [search, year, showConsumption, includeArchived, selectedBuildingId, onlyZeros]);

  const monthCopyFlags = useMemo(() => {
    const flags = {};
    for (const m of months) {
      flags[m.month] = rows.some((row) => row.values?.[m.month]?.can_copy);
    }
    return flags;
  }, [months, rows]);

  const title = useMemo(() => {
    const name = data?.building_name || data?.building_address || selectedBuilding?.name || selectedBuilding?.address;
    return name ? `Показания РТЭ — ${name}` : 'Показания РТЭ';
  }, [data, selectedBuilding]);

  const toggleApartment = (apartmentId) => {
    setCollapsedApartments((prev) => ({
      ...prev,
      [apartmentId]: !prev[apartmentId],
    }));
  };

  const toggleAllGroups = () => {
    if (!multiGroups.length) return;
    const nextCollapsed = !allCollapsed;
    const next = { ...collapsedApartments };
    for (const g of multiGroups) {
      next[g.apartmentId] = nextCollapsed;
    }
    setCollapsedApartments(next);
  };

  const runCopy = async ({ month, deviceId = null }) => {
    if (!selectedBuildingId) return;
    const key = deviceId ? `copy-${deviceId}-${month}` : `copy-month-${month}`;
    setActionBusy(key);
    setError('');
    try {
      const res = await heatApi.copyPreviousReadingsMonth(selectedBuildingId, {
        month,
        device_id: deviceId,
      });
      await load({ silent: true });
      showNotice(res.message || 'Показания скопированы');
    } catch (e) {
      setError(formatApiError(e, 'Не удалось скопировать показания'));
    } finally {
      setActionBusy('');
    }
  };

  const runLock = async (month, locked) => {
    if (!selectedBuildingId) return;
    setActionBusy(`lock-${month}`);
    setError('');
    setLockConfirm(null);
    try {
      const res = await heatApi.lockReadingsMonth(selectedBuildingId, { month, locked });
      await load({ silent: true });
      showNotice(res.message || (locked ? 'Месяц заблокирован' : 'Месяц разблокирован'));
    } catch (e) {
      setError(formatApiError(e, 'Не удалось изменить блокировку'));
    } finally {
      setActionBusy('');
    }
  };

  const requestLockToggle = (month, locked) => {
    if (locked) {
      setLockConfirm({ month, locked: true });
      return;
    }
    runLock(month, false);
  };

  const handleHeatModeChange = async (apartmentId, byArea) => {
    if (!apartmentId) return;
    const previousRows = data?.rows || [];
    const current = previousRows.find((row) => String(row.apartment_id) === String(apartmentId));
    if (current && Boolean(current.heat_calculate_by_area) === Boolean(byArea)) return;
    setSavingModeApartmentId(String(apartmentId));
    setError('');
    setData((prev) => (
      prev
        ? { ...prev, rows: applyApartmentHeatMode(prev.rows, apartmentId, byArea) }
        : prev
    ));
    try {
      await apartmentsApi.update(apartmentId, { heat_calculate_by_area: byArea });
      showNotice(byArea ? 'Расчёт: по площади' : 'Расчёт: по УРТ');
    } catch (e) {
      setData((prev) => (prev ? { ...prev, rows: previousRows } : prev));
      setError(formatApiError(e, 'Не удалось изменить режим расчёта квартиры'));
    } finally {
      setSavingModeApartmentId('');
    }
  };

  const renderHeatModeControl = (apartmentId, byArea, { stopRowClick = false } = {}) => {
    const saving = savingModeApartmentId === String(apartmentId);
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        onClick={stopRowClick ? (e) => e.stopPropagation() : undefined}
      >
        <Tooltip
          title={
            byArea
              ? 'Сейчас по площади (без УРТ). Нажмите «УРТ», чтобы считать по приборам.'
              : 'Сейчас по УРТ. Нажмите «пл.», чтобы считать по площади.'
          }
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={byArea ? 'area' : 'urt'}
            disabled={saving || Boolean(actionBusy)}
            onChange={(_, next) => {
              if (!next) return;
              handleHeatModeChange(apartmentId, next === 'area');
            }}
            sx={{
              flexShrink: 0,
              '& .MuiToggleButton-root': {
                px: 0.7,
                py: 0.15,
                minWidth: 36,
                fontSize: 11,
                lineHeight: 1.2,
                textTransform: 'none',
                fontWeight: 700,
                borderColor: 'divider',
              },
            }}
          >
            <ToggleButton value="urt" color="success" aria-label="Расчёт по УРТ">
              УРТ
            </ToggleButton>
            <ToggleButton value="area" color="warning" aria-label="Расчёт по площади">
              пл.
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
        {saving ? <CircularProgress size={12} /> : null}
      </Stack>
    );
  };

  const handleTableScroll = (event) => {
    const nextTop = event.currentTarget.scrollTop;
    if (scrollRafRef.current) window.cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = window.requestAnimationFrame(() => {
      setScrollTop(nextTop);
      scrollRafRef.current = 0;
    });
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const csvRows = rows.map((row) => {
      const base = {
        Корпус: row.corp_name || '',
        Квартира: row.apartment_number || '',
        Расчёт: row.heat_accounting_label || (row.heat_calculate_by_area ? 'по площади' : 'по УРТ'),
        '№ РТЭ': row.serial_number || '',
        Комната: row.room_label || row.placement || '',
        Площадь: row.area ?? '',
        'Пониж. коэфф.': row.heat_reduction_coefficient ?? '',
        'Коэфф. мощности': row.heat_coefficient ?? row.power_watts ?? '',
      };
      for (const m of months) {
        base[formatMonthLabel(m.month)] = row.values?.[m.month]?.value ?? '';
      }
      return base;
    });
    const csv = generateCsv(csvRows);
    const slug = String(data?.building_name || selectedBuildingId || 'building')
      .replace(/[^\w\-]+/g, '_')
      .slice(0, 40);
    downloadCsv(csv, `rte-readings_${slug}_${year}.csv`);
  };

  const colCount = 5 + months.length;
  const showInitialLoader = loading && !data;

  const renderMonthCells = (row, rowBg) => months.map((m) => {
    const cell = row.values?.[m.month];
    const busy = actionBusy === `copy-${row.device_id}-${m.month}`;
    const muted = Boolean(cell?.is_carried) && !showConsumption;
    const copied = Boolean(cell?.is_copied) && !showConsumption;
    return (
      <TableCell
        key={`${row.device_id}-${m.month}`}
        align="right"
        sx={{
          bgcolor: m.locked ? lockedBg : copied ? copiedBg : rowBg,
          py: 0.5,
        }}
      >
        <Stack direction="row" spacing={0.15} justifyContent="flex-end" alignItems="center">
          <Tooltip title={cellTooltip(cell, m.locked)}>
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontVariantNumeric: 'tabular-nums',
                color: muted ? 'text.secondary' : 'text.primary',
                fontStyle: muted ? 'italic' : 'normal',
                fontWeight: copied ? 600 : 400,
              }}
            >
              {formatNumber(cell?.value, 3)}
            </Typography>
          </Tooltip>
          {cell?.can_copy && !m.locked ? (
            <Tooltip title="Вставить показание прошлого месяца">
              <span>
                <IconButton
                  size="small"
                  disabled={Boolean(actionBusy)}
                  onClick={() => runCopy({ month: m.month, deviceId: row.device_id })}
                  sx={{ p: 0.2 }}
                >
                  {busy ? <CircularProgress size={11} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      </TableCell>
    );
  });

  return (
    <div
      className="home-container page-animate"
      style={{
        width: '100%',
        maxWidth: 'calc(100vw - var(--sidebar-current-width) - 40px)',
        margin: '0 auto',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 24,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
            <button type="button" className="rc-btn" onClick={() => navigate(-1)}>
              Назад
            </button>
            <Typography variant="h5" noWrap title={title}>
              {title}
            </Typography>
            {loading && data ? <CircularProgress size={18} /> : null}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportCsv}
              disabled={!rows.length}
            >
              CSV
            </Button>
            <IconButton onClick={() => load()} disabled={loading || !selectedBuildingId} title="Обновить">
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
              <Autocomplete
                sx={{ minWidth: 280, flex: 1, maxWidth: 520 }}
                options={buildingOptions}
                loading={buildingsLoading}
                value={selectedBuilding}
                onChange={(_, value) => handleBuildingChange(value)}
                getOptionLabel={(option) => buildingLabel(option)}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.name || 'Без названия'}</Typography>
                      {option.address ? (
                        <Typography variant="caption" color="text.secondary">
                          {option.address}
                        </Typography>
                      ) : null}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Дом"
                    placeholder="Выберите дом с УРТ"
                  />
                )}
              />
              <TextField
                select
                size="small"
                label="Год"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                sx={{ minWidth: 110 }}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <TextField
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Квартира, № РТЭ…"
                sx={{ minWidth: 190 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Очистить поиск"
                        onClick={() => {
                          setSearchInput('');
                          setSearch('');
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <Tooltip title={zeroRowsCount ? `Строк с нулевыми показаниями: ${zeroRowsCount}` : 'Нет строк с нулевыми показаниями'}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={onlyZeros}
                      disabled={!zeroRowsCount && !onlyZeros}
                      onChange={(e) => setOnlyZeros(e.target.checked)}
                      color="error"
                    />
                  }
                  label="Только нули"
                />
              </Tooltip>
              {multiGroups.length ? (
                <Tooltip title={allCollapsed ? 'Развернуть все группы' : 'Свернуть все группы'}>
                  <IconButton size="small" onClick={toggleAllGroups}>
                    {allCollapsed ? <UnfoldMoreIcon fontSize="small" /> : <UnfoldLessIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              ) : null}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showConsumption}
                    onChange={(e) => setShowConsumption(e.target.checked)}
                  />
                }
                label="Расход"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={includeArchived}
                    onChange={(e) => setIncludeArchived(e.target.checked)}
                  />
                }
                label="Архивные"
              />
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
        {notice ? (
          <Alert severity="success" onClose={() => setNotice('')}>
            {notice}
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {!selectedBuildingId ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">Выберите дом, чтобы увидеть показания РТЭ</Typography>
            </Box>
          ) : showInitialLoader ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer
              ref={tableContainerRef}
              onScroll={handleTableScroll}
              sx={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 980,
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  tableLayout: 'fixed',
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: COL_APARTMENT,
                        minWidth: COL_APARTMENT,
                        ...opaqueStickySx(0, 8, headerBg, { isHeader: true }),
                      }}
                    >
                      Помещение / режим
                    </TableCell>
                    <TableCell
                      sx={{
                        width: COL_SERIAL,
                        minWidth: COL_SERIAL,
                        ...opaqueStickySx(COL_APARTMENT, 8, headerBg, {
                          withEdgeShadow: true,
                          isHeader: true,
                        }),
                      }}
                    >
                      № РТЭ
                    </TableCell>
                    <TableCell align="right" sx={{ width: 84, bgcolor: headerBg }}>
                      Площадь
                    </TableCell>
                    <TableCell align="center" sx={{ width: 88, bgcolor: headerBg }}>
                      Пониж.
                    </TableCell>
                    <TableCell align="right" sx={{ width: 88, bgcolor: headerBg }}>
                      Мощн.
                    </TableCell>
                    {months.map((m) => {
                      const canBulkCopy = monthCopyFlags[m.month];
                      return (
                        <TableCell
                          key={m.month}
                          align="center"
                          sx={{
                            width: 96,
                            minWidth: 96,
                            bgcolor: m.locked ? lockedBg : headerBg,
                            borderBottom: m.locked ? '2px solid' : undefined,
                            borderBottomColor: m.locked ? 'warning.main' : undefined,
                          }}
                        >
                          <Stack spacing={0.15} alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                              {formatMonthShort(m.month)}
                            </Typography>
                            <Stack direction="row" spacing={0}>
                              <Tooltip title={m.locked ? 'Разблокировать показания' : 'Заблокировать показания'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color={m.locked ? 'warning' : 'default'}
                                    disabled={Boolean(actionBusy) || m.can_lock === false}
                                    onClick={() => requestLockToggle(m.month, !m.locked)}
                                    sx={{ p: 0.35 }}
                                  >
                                    {m.locked ? (
                                      <LockOutlinedIcon sx={{ fontSize: 15 }} />
                                    ) : (
                                      <LockOpenOutlinedIcon sx={{ fontSize: 15 }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Скопировать показания предыдущего месяца во все пустые ячейки">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!canBulkCopy || m.locked || Boolean(actionBusy)}
                                    onClick={() => runCopy({ month: m.month })}
                                    sx={{ p: 0.35 }}
                                  >
                                    <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!flatItems.length ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center">
                        {rows.length && (search.trim() || onlyZeros)
                          ? 'Ничего не найдено по фильтру'
                          : 'Нет приборов УРТ за выбранный год'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {visibleRange.topPad > 0 ? (
                        <TableRow aria-hidden sx={{ height: visibleRange.topPad }}>
                          <TableCell
                            colSpan={colCount}
                            sx={{ p: 0, border: 0, height: visibleRange.topPad, lineHeight: 0 }}
                          />
                        </TableRow>
                      ) : null}
                      {visibleItems.map((item) => {
                        const tint = item.tintKey === 0 ? tintA : tintB;
                        if (item.type === 'group') {
                          const { group } = item;
                          const collapsed = Boolean(collapsedApartments[group.apartmentId]);
                          const groupAllZero = group.devices.every((device) => isAllZeroRow(device, months));
                          const groupBg = groupAllZero ? zeroTint : tint;
                          return (
                            <TableRow
                              key={item.key}
                              onClick={() => toggleApartment(group.apartmentId)}
                              sx={{
                                height: GROUP_ROW_HEIGHT,
                                bgcolor: groupBg,
                                borderLeft: '3px solid',
                                borderLeftColor: groupAllZero ? 'error.main' : 'primary.main',
                                cursor: 'pointer',
                                '& td': { py: 0.75 },
                              }}
                            >
                              <TableCell
                                sx={{
                                  width: COL_APARTMENT,
                                  minWidth: COL_APARTMENT,
                                  ...opaqueStickySx(0, 3, groupBg),
                                }}
                              >
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleApartment(group.apartmentId);
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                                  </IconButton>
                                  <Typography variant="body2" fontWeight={700}>
                                    {group.apartmentNumber || '—'}
                                    {group.corpName ? (
                                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                        {group.corpName}
                                      </Typography>
                                    ) : null}
                                  </Typography>
                                  {renderHeatModeControl(group.apartmentId, group.heatCalculateByArea, {
                                    stopRowClick: true,
                                  })}
                                  <Chip
                                    size="small"
                                    label={`${group.devices.length} УРТ`}
                                    variant="outlined"
                                    sx={{ height: 22 }}
                                  />
                                </Stack>
                              </TableCell>
                              <TableCell
                                sx={{
                                  width: COL_SERIAL,
                                  minWidth: COL_SERIAL,
                                  ...opaqueStickySx(COL_APARTMENT, 3, groupBg, { withEdgeShadow: true }),
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  {formatNumber(group.area, 1)} м²
                                </Typography>
                              </TableCell>
                              <TableCell colSpan={colCount - 2} sx={{ bgcolor: groupBg }} />
                            </TableRow>
                          );
                        }

                        const { row, multi } = item;
                        const allZero = isAllZeroRow(row, months);
                        const rowBg = allZero ? zeroTint : multi ? tint : paperBg;
                        return (
                          <TableRow
                            key={item.key}
                            hover
                            title={allZero ? 'Все показания года равны 0' : undefined}
                            sx={{
                              height: DEVICE_ROW_HEIGHT,
                              opacity: row.archived ? 0.7 : 1,
                              bgcolor: rowBg,
                              borderLeft: multi || allZero ? '3px solid' : undefined,
                              borderLeftColor: allZero ? 'error.light' : multi ? 'primary.light' : undefined,
                              '& td': { py: 0.55 },
                            }}
                          >
                            <TableCell
                              sx={{
                                width: COL_APARTMENT,
                                minWidth: COL_APARTMENT,
                                ...opaqueStickySx(0, 3, rowBg),
                              }}
                            >
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                {!multi ? (
                                  <>
                                    <Typography variant="body2" fontWeight={600}>
                                      {row.apartment_number || '—'}
                                    </Typography>
                                    {row.corp_name ? (
                                      <Typography variant="caption" color="text.secondary">
                                        {row.corp_name}
                                      </Typography>
                                    ) : null}
                                    {renderHeatModeControl(
                                      row.apartment_id,
                                      row.heat_calculate_by_area,
                                    )}
                                  </>
                                ) : (
                                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
                                    {row.room_label || row.placement || 'прибор'}
                                  </Typography>
                                )}
                                <Tooltip title="Открыть квартиру">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      navigate(`/apartments/${row.apartment_id}?deviceId=${row.device_id}`)
                                    }
                                    sx={{ p: 0.25 }}
                                  >
                                    <EditOutlinedIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                                {row.archived ? (
                                  <Chip size="small" label="архив" variant="outlined" sx={{ height: 20 }} />
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell
                              sx={{
                                width: COL_SERIAL,
                                minWidth: COL_SERIAL,
                                ...opaqueStickySx(COL_APARTMENT, 3, rowBg, { withEdgeShadow: true }),
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                              >
                                {row.serial_number || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ bgcolor: rowBg }}>
                              {multi ? '—' : formatNumber(row.area, 1)}
                            </TableCell>
                            <TableCell align="center" sx={{ bgcolor: rowBg }}>
                              {row.heat_reduction_coefficient != null
                                ? formatNumber(row.heat_reduction_coefficient, 2)
                                : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ bgcolor: rowBg }}>
                              {formatNumber(row.heat_coefficient ?? row.power_watts, 3)}
                            </TableCell>
                            {renderMonthCells(row, rowBg)}
                          </TableRow>
                        );
                      })}
                      {visibleRange.bottomPad > 0 ? (
                        <TableRow aria-hidden sx={{ height: visibleRange.bottomPad }}>
                          <TableCell
                            colSpan={colCount}
                            sx={{ p: 0, border: 0, height: visibleRange.bottomPad, lineHeight: 0 }}
                          />
                        </TableRow>
                      ) : null}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {filteredRows.length} приб.
            {filteredRows.length !== rows.length ? ` / ${rows.length}` : ''}
            {' · '}{groups.length} кв.
            {zeroRowsCount ? ` · нулей: ${zeroRowsCount}` : ''}
            {flatItems.length > visibleItems.length
              ? ` · видно ${visibleItems.length}/${flatItems.length}`
              : ''}
            {' · '}
            {showConsumption ? 'режим расхода' : 'курсив = перенос, зелёный = copy, красный = нули'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {year}
          </Typography>
        </Stack>
      </Stack>

      <Dialog open={Boolean(lockConfirm)} onClose={() => setLockConfirm(null)}>
        <DialogTitle>Заблокировать показания?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {lockConfirm
              ? `Месяц ${formatMonthLabel(lockConfirm.month)} будет закрыт для копирования и правок показаний.`
              : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockConfirm(null)}>Отмена</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={Boolean(actionBusy)}
            onClick={() => lockConfirm && runLock(lockConfirm.month, true)}
          >
            Заблокировать
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
